import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import VanillaContextMenu from 'vanilla-context-menu';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import SetOrderLimitsDrawer from 'components/drawers/kitchen-capacity-drawers/set-order-limits-drawer/set-order-limits-drawer.component';
import { useUtilityDrawer } from 'components/drawers/utility-drawer/utility-drawer.hooks';
import CustomHeaderToolbar from './custom-header-toolbar/custom-header-toolbar.component';
import orderLimitsEvent from './order-limits-event/order-limits-event.component';
import { useKitchenCapacityContext } from '../kitchen-capacity.context';
import { Stack, useTheme } from '@mui/material';
import '../index.css';
import { convertToSetOrderLimitsFormData } from './order-limits.utils';
import FormSection from 'components/form-section/form-section.component';
import StandardHoursList from './standard-hours-list/standard-hours-list.component';

export const OrderLimitsCalendar = () => {
  const { t } = useTranslation();
  const {
    orderLimits: {
      orderLimitsEvents,
      orderLimitsRef,
      addDummyEvent,
      eventsSet,
    },
  } = useKitchenCapacityContext();

  const setOrderLimitsDrawer = useUtilityDrawer();

  const theme = useTheme();

  const root = document.documentElement;
  root.style.setProperty(
    '--fc-page-bg-color',
    theme.palette.background.default,
  );

  root.style.setProperty(
    '--context-menu-item-text-color',
    theme.palette.text.primary,
  );

  root.style.setProperty(
    '--context-menu-item-hover-bg-color',
    theme.palette.action.hover,
  );

  const handleEventOverlap = (stillEvent, movingEvent) =>
    ['background', 'none'].includes(stillEvent.display) ||
    stillEvent.extendedProps.orderLimitsType !==
      movingEvent.extendedProps.orderLimitsType;

  return (
    <Stack direction="row" gap={2}>
      <Stack minWidth={320} sx={{ display: { xs: 'none', md: 'block' } }}>
        <StandardHoursList />
      </Stack>

      <FormSection
        title={t('KitchenCapacity.orderLimits.calendar.formSection.title')}
        subtitle={t(
          'KitchenCapacity.orderLimits.calendar.formSection.subtitle',
        )}
        sx={{ maxWidth: 'auto' }}
        contentSx={{ padding: '0 16px 16px 16px' }}
      >
        <Stack data-testid="OrderLimits.Calendar">
          <CustomHeaderToolbar />
          <FullCalendar
            plugins={[timeGridPlugin, interactionPlugin, listPlugin]}
            initialView="timeGridWeek" // Set initial view to Week
            now={DateTime.local().toJSDate()}
            initialEvents={orderLimitsEvents}
            eventOrder="-title" // otherwise events can swap columns and we don't want that
            eventsSet={eventsSet}
            allDaySlot={false}
            headerToolbar={false}
            editable
            eventResizableFromStart
            ref={orderLimitsRef}
            eventOverlap={handleEventOverlap}
            dayHeaderFormat={{ weekday: 'short' }}
            dateClick={dateSelect => {
              const start = DateTime.fromJSDate(dateSelect.date);
              const end = start.plus({ minutes: 15 });

              const dummyEvent = addDummyEvent(start, end);

              setOrderLimitsDrawer.toggleWithData(
                convertToSetOrderLimitsFormData({
                  title: dummyEvent.title,
                  start,
                  end,
                  kitchenThrottleValues:
                    dummyEvent.extendedProps.kitchenThrottleValues,
                }),
              );
            }}
            eventClick={({ event }) => {
              if (!['background', 'none'].includes(event.display)) {
                setOrderLimitsDrawer.toggleWithData(
                  convertToSetOrderLimitsFormData({
                    id: event.id,
                    title: event.title,
                    start: DateTime.fromJSDate(event.start),
                    end: DateTime.fromJSDate(event.end),
                    kitchenThrottleValues:
                      event.extendedProps.kitchenThrottleValues,
                  }),
                );
              }
            }}
            scrollTime="00:00:00"
            slotDuration="00:15:00"
            eventDidMount={({ event, el }) => {
              if (!['background', 'none'].includes(event.display)) {
                new VanillaContextMenu({
                  scope: el,
                  normalizePosition: false,
                  transitionDuration: 0,
                  customClass: 'context-menu-custom',
                  customThemeClass: 'context-menu-item-custom',
                  menuItems: [
                    {
                      label: t(
                        'KitchenCapacity.orderLimits.calendar.event.menuItems.edit',
                      ),
                      callback: () => {
                        setOrderLimitsDrawer.toggleWithData(
                          convertToSetOrderLimitsFormData({
                            id: event.id,
                            title: event.title,
                            start: DateTime.fromJSDate(event.start),
                            end: DateTime.fromJSDate(event.end),
                            kitchenThrottleValues:
                              event.extendedProps.kitchenThrottleValues,
                          }),
                        );
                      },
                    },
                    'hr',
                    {
                      label: t(
                        'KitchenCapacity.orderLimits.calendar.event.menuItems.delete',
                      ),
                      callback: () => {
                        event.remove();
                      },
                    },
                  ],
                });
              }
            }}
            eventContent={orderLimitsEvent}
            height="auto"
            listDaySideFormat={false}
          />
          <SetOrderLimitsDrawer {...setOrderLimitsDrawer} />
        </Stack>
      </FormSection>
    </Stack>
  );
};
export default OrderLimitsCalendar;
